import { SignIn } from './routes/SignIn';
import { Home } from './routes/Home';
import { Project } from './routes/Project';
import { Task } from './routes/Task';
import { Profile } from './routes/Profile';
import { Settings } from './routes/Settings';
import { Time } from './routes/Time';
import { People } from './routes/People';
import { Clients } from './routes/Clients';
import { Billing } from './routes/Billing';
import { Document } from './routes/Document';
import { Client } from './routes/Client';
import { afterSignIn, match, navigate, usePath, useQuery } from './router';
import { useSession } from './session';
import { useEffect } from 'react';

export function App() {
  const { user, loading } = useSession();
  const path = usePath();
  const query = useQuery();
  const hasToken = query.has('token');
  const next = query.get('next');

  // Anyone without a session lands on the sign-in page, whatever they asked
  // for. Where they were going is kept, so the link in the mail brings them
  // back to it.
  useEffect(() => {
    if (loading) return;
    if (!user && !path.startsWith('/signin')) {
      const here = path + window.location.search;
      navigate(path === '/' ? '/signin' : `/signin?next=${encodeURIComponent(here)}`, true);
    }
    if (user && path.startsWith('/signin') && !hasToken) {
      navigate(afterSignIn(next), true);
    }
    // Your own page is where the app opens. The project list lived at / and
    // its filters were the query string, so a kept link with filters still
    // goes to the list.
    if (user && path === '/') {
      const search = window.location.search;
      navigate(search ? `/projects${search}` : '/profile', true);
    }
  }, [user, loading, path, hasToken, next]);

  // A link in the mail wins over whatever session this browser already has.
  // Two people share a laptop, or somebody is signed in as themselves and
  // opens a link for a colleague; landing silently on the wrong account's
  // home page is worse than any of the alternatives.
  if (path.startsWith('/signin') && hasToken) {
    return <SignIn />;
  }

  if (loading) return null;
  if (!user) return <SignIn />;
  if (path === '/') return null;

  const project = match('/projects/:id', path);
  if (project) return <Project id={Number(project.id)} />;

  const task = match('/tasks/:id', path);
  if (task) return <Task id={Number(task.id)} />;

  if (match('/projects', path)) return <Home />;
  if (match('/profile', path)) return <Profile />;
  if (match('/settings', path)) return <Settings />;
  if (match('/time', path) && user.isStaff) return <Time />;
  if (match('/people', path) && user.isStaff) return <People />;
  if (match('/clients', path) && user.isStaff) return <Clients />;
  if (match('/billing', path) && user.isStaff) return <Billing />;

  const client = match('/clients/:id', path);
  if (client) return <Client id={Number(client.id)} />;

  const doc = match('/documents/:id', path);
  if (doc && user.isStaff) return <Document id={Number(doc.id)} />;

  return <Profile />;
}
